// src/App.jsx

import React, { useEffect, useState } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './AuthContext';
import { ProtectedRoute, UnauthorizedPage } from './components/ProtectedRoute';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import TableManagement from './pages/TableManagement';
import Billing from './pages/Billing';
import MenuManagement from './pages/MenuManagement';
import KitchenDisplay from './pages/KitchenDisplay';
import OrderHistory from './pages/OrderHistory';
import Inventory from './pages/Inventory';
import Accounts from './pages/Accounts';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import StaffManagement from './pages/StaffManagement';
import LoginPage from './pages/Login';
import SignupPage from './pages/Signup';

// Loading Component
const AppLoading = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    background: '#0a0a0a',
    flexDirection: 'column',
    gap: '20px',
  }}>
    <div style={{
      width: '60px',
      height: '60px',
      border: '4px solid rgba(255,107,53,0.1)',
      borderTop: '4px solid #ff6b35',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
    }} />
    <div style={{
      color: 'rgba(255,255,255,0.6)',
      fontSize: '14px',
      fontWeight: 500,
      letterSpacing: '1px',
    }}>
      Loading RAATH DINE...
    </div>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

// Public Route with role-based redirect
const PublicRoute = ({ children }) => {
  const savedUser = localStorage.getItem('raath_dine_user');

  if (savedUser) {
    try {
      const user = JSON.parse(savedUser);
      const pages = user.pages || [];
      
      if (pages.includes('dashboard')) return <Navigate to="/dashboard" replace />;
      if (pages.includes('pos')) return <Navigate to="/billing" replace />;
      if (pages.includes('kitchen')) return <Navigate to="/kitchen" replace />;
      if (pages.length > 0) return <Navigate to={`/${pages[0]}`} replace />;
    } catch (e) {
      return <Navigate to="/dashboard" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Route wrapper with Layout
const ProtectedLayoutRoute = ({ element, requiredPage, requiredPermission }) => {
  return (
    <ProtectedRoute requiredPage={requiredPage} requiredPermission={requiredPermission}>
      <Layout>
        {element}
      </Layout>
    </ProtectedRoute>
  );
};

function App() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return <AppLoading />;
  }

  return (
    <Router>
      <AuthProvider>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />

          {/* Protected Routes with Layout */}
          <Route path="/dashboard" element={<ProtectedLayoutRoute element={<Dashboard />} requiredPage="dashboard" />} />
          <Route path="/billing" element={<ProtectedLayoutRoute element={<Billing />} requiredPage="pos" />} />
          <Route path="/pos" element={<ProtectedLayoutRoute element={<Billing />} requiredPage="pos" />} />
          <Route path="/tables" element={<ProtectedLayoutRoute element={<TableManagement />} requiredPage="tables" />} />
          <Route path="/menu" element={<ProtectedLayoutRoute element={<MenuManagement />} requiredPage="menu" requiredPermission="manage_menu" />} />
          <Route path="/kitchen" element={<ProtectedLayoutRoute element={<KitchenDisplay />} requiredPage="kitchen" />} />
          <Route path="/orders" element={<ProtectedLayoutRoute element={<OrderHistory />} requiredPage="orders" />} />
          <Route path="/inventory" element={<ProtectedLayoutRoute element={<Inventory />} requiredPage="inventory" requiredPermission="manage_inventory" />} />
          <Route path="/accounts" element={<ProtectedLayoutRoute element={<Accounts />} requiredPage="accounts" requiredPermission="view_accounts" />} />
          <Route path="/reports" element={<ProtectedLayoutRoute element={<Reports />} requiredPage="reports" requiredPermission="view_reports" />} />
          <Route path="/staff" element={<ProtectedLayoutRoute element={<StaffManagement />} requiredPage="staff" requiredPermission="manage_staff" />} />
          <Route path="/settings" element={<ProtectedLayoutRoute element={<Settings />} requiredPage="settings" requiredPermission="manage_settings" />} />
          <Route path="/backup" element={<ProtectedLayoutRoute element={<Settings />} requiredPage="backup" requiredPermission="manage_backup" />} />

          {/* Default & Catch All */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;